import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { SquaresFour, ArrowUpRight, Check, Clock, Package, ChartBar, ShoppingCart } from "@phosphor-icons/react";

/**
 * LOM Group product switcher.
 * Renders a small "apps" button that opens a panel listing all LOM Group
 * products so users can move between them.
 *
 * Add/edit products by editing the PRODUCTS array below.
 */
const PRODUCTS = [
  {
    id: "jaminventra",
    name: "JamInventra",
    tagline: "Inventory management",
    url: "https://www.lomgroupja.com/jaminventra",
    external: true,
    status: "available",
    accent: "#0EA5E9", // sky-500
    Icon: Package,
  },
  {
    id: "timetrack",
    name: "LOM TimeTrack",
    tagline: "Staff attendance & time",
    url: "/",
    current: true,
    status: "current",
    accent: "#00A651", // Jamaican green
    Icon: Clock,
  },
  {
    id: "procure",
    name: "LOM Procure",
    tagline: "Purchase & vendor mgmt",
    url: null,
    status: "coming_soon",
    accent: "#F59E0B", // amber-500
    Icon: ShoppingCart,
  },
  {
    id: "analytics",
    name: "LOM Analytics",
    tagline: "Business intelligence",
    url: null,
    status: "coming_soon",
    accent: "#8B5CF6", // violet-500
    Icon: ChartBar,
  },
];

export default function ProductSwitcher({ variant = "light" }) {
  const isDark = variant === "dark";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="product-switcher-trigger"
          aria-label="LOM Group product switcher"
          className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-sm border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 ${
            isDark
              ? "border-white/15 text-white hover:bg-white/10"
              : "border-zinc-200 text-zinc-700 hover:bg-zinc-100"
          }`}
        >
          <SquaresFour size={16} weight="bold" />
          <span className="text-[11px] font-semibold uppercase tracking-wider hidden sm:inline">
            Apps
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        data-testid="product-switcher-panel"
        className="w-80 p-0 border border-zinc-200 shadow-xl rounded-sm"
      >
        {/* Panel header */}
        <div className="px-4 pt-4 pb-3 border-b border-zinc-100">
          <div className="text-[10px] font-semibold tracking-[0.22em] text-zinc-400 uppercase">
            LOM Group
          </div>
          <div className="text-sm font-bold text-zinc-950 mt-0.5">
            Business software suite
          </div>
        </div>

        {/* Products */}
        <ul className="p-1.5" role="menu">
          {PRODUCTS.map((p) => (
            <ProductRow key={p.id} product={p} />
          ))}
        </ul>

        {/* Footer */}
        <div className="border-t border-zinc-100 px-4 py-2.5 flex items-center justify-between">
          <span className="text-[11px] text-zinc-500">
            More coming soon
          </span>
          <a
            href="https://www.lomgroupja.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="product-switcher-lomgroup-link"
            className="text-[11px] font-semibold text-zinc-950 hover:text-zinc-700 inline-flex items-center gap-1"
          >
            lomgroupja.com <ArrowUpRight size={11} weight="bold" />
          </a>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ProductRow({ product }) {
  const { name, tagline, url, external, status, accent, Icon, current } = product;
  const disabled = status === "coming_soon";

  const inner = (
    <div className="flex items-center gap-3 px-2.5 py-2.5 rounded-sm w-full text-left">
      {/* Icon tile */}
      <div
        className="flex-shrink-0 w-9 h-9 rounded-sm flex items-center justify-center"
        style={{ backgroundColor: `${accent}18`, color: accent }}
      >
        <Icon size={18} weight="bold" />
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-zinc-950 truncate">
            {name}
          </span>
          {current && (
            <span
              data-testid={`product-current-${product.id}`}
              className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-sm bg-zinc-950 text-white"
            >
              Current
            </span>
          )}
          {status === "coming_soon" && (
            <span className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-500">
              Soon
            </span>
          )}
        </div>
        <div className="text-[11px] text-zinc-500 truncate">{tagline}</div>
      </div>

      {/* Right side indicator */}
      <div className="flex-shrink-0 text-zinc-400">
        {current ? (
          <Check size={14} weight="bold" className="text-zinc-950" />
        ) : external && !disabled ? (
          <ArrowUpRight size={14} weight="bold" />
        ) : null}
      </div>
    </div>
  );

  if (disabled) {
    return (
      <li>
        <div
          data-testid={`product-row-${product.id}`}
          aria-disabled="true"
          className="opacity-55 cursor-not-allowed"
        >
          {inner}
        </div>
      </li>
    );
  }

  if (external) {
    return (
      <li>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`product-row-${product.id}`}
          className="block hover:bg-zinc-50 rounded-sm transition-colors"
        >
          {inner}
        </a>
      </li>
    );
  }

  // internal / current
  return (
    <li>
      <div
        data-testid={`product-row-${product.id}`}
        className="bg-zinc-50 rounded-sm"
      >
        {inner}
      </div>
    </li>
  );
}
